import React, { useCallback, useRef, useState } from 'react'

export interface TimeValue {
    hour?: number
    minute?: number
    second?: number
}

export interface TimeFieldProps {
    variant?: 'primary' | 'secondary'
    label?: string
    required?: boolean
    description?: string
    errorMessage?: string
    isInvalid?: boolean
    disabled?: boolean
    showLabel?: boolean
    showDescription?: boolean
    showSeconds?: boolean
    hourCycle?: 12 | 24
    value?: TimeValue
    defaultValue?: TimeValue
    onChange?: (value: TimeValue) => void
    style?: React.CSSProperties
    timezone?: string
}

const pad = (n: number | undefined): string => {
    if (n === undefined) return '--'
    return n.toString().padStart(2, '0')
}

const clamp = (val: number, min: number, max: number): number => Math.max(min, Math.min(max, val))

export const TimeField = ({
    variant = 'primary',
    label,
    required = false,
    description,
    errorMessage,
    isInvalid = false,
    disabled = false,
    showLabel = true,
    showDescription = true,
    showSeconds = false,
    hourCycle = 12,
    value: controlledValue,
    defaultValue,
    onChange,
    style,
    timezone = 'EST',
}: TimeFieldProps) => {
    const isSecondary = variant === 'secondary'
    const [internalValue, setInternalValue] = useState<TimeValue>(defaultValue ?? {})
    const [activeSegment, setActiveSegment] = useState<string | null>(null)

    const value = controlledValue ?? internalValue
    const is12 = hourCycle === 12

    const hourRef = useRef<HTMLInputElement>(null)
    const minuteRef = useRef<HTMLInputElement>(null)
    const secondRef = useRef<HTMLInputElement>(null)

    const update = useCallback(
        (partial: Partial<TimeValue>) => {
            const next = { ...value, ...partial }
            if (!controlledValue) setInternalValue(next)
            onChange?.(next)
        },
        [value, controlledValue, onChange]
    )

    const displayHour = value.hour !== undefined ? (is12 ? (value.hour === 0 ? 12 : value.hour > 12 ? value.hour - 12 : value.hour) : value.hour) : undefined

    const period = value.hour !== undefined ? (value.hour >= 12 ? 'PM' : 'AM') : 'AM'

    const togglePeriod = () => {
        if (value.hour === undefined) return
        update({ hour: value.hour >= 12 ? value.hour - 12 : value.hour + 12 })
    }

    // In 12-hour mode a typed hour is what is displayed (1-12) and keeps the current AM/PM, so typing in the afternoon never lands on the
    // other half of the day. The arrows walk the clock itself: 11 AM goes up to 12 PM, and they stop at the ends of the day. The stored
    // `hour` stays 0-23.
    const to24 = (hour12: number) => (period === 'PM' ? (hour12 % 12) + 12 : hour12 % 12)

    const handleSegmentKey = (segment: 'hour' | 'minute' | 'second', e: React.KeyboardEvent) => {
        const isDisplayedHour = segment === 'hour' && is12
        const current = isDisplayedHour ? displayHour : value[segment]
        const maxMap = { hour: is12 ? 12 : 23, minute: 59, second: 59 }
        const minMap = { hour: is12 ? 1 : 0, minute: 0, second: 0 }
        const commit = (next: number) => {
            const clamped = clamp(next, minMap[segment], maxMap[segment])
            update({ [segment]: isDisplayedHour ? to24(clamped) : clamped })
        }

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault()
            const step = e.key === 'ArrowUp' ? 1 : -1
            if (isDisplayedHour && value.hour !== undefined) update({ hour: clamp(value.hour + step, 0, 23) })
            else commit(current !== undefined ? current + step : step > 0 ? minMap[segment] : maxMap[segment])
        } else if (e.key === 'Tab') {
            // natural tab
        } else if (/^\d$/.test(e.key)) {
            e.preventDefault()
            const digit = parseInt(e.key)
            const prev = current !== undefined ? current : 0
            let next = prev * 10 + digit
            if (next > maxMap[segment]) next = digit
            commit(next)
        }
    }

    const segmentClass = (seg: string) =>
        `w-6 rounded-sm border-none px-0.5 text-center text-sm font-normal leading-5 caret-transparent outline-none
        ${disabled ? 'cursor-not-allowed' : 'cursor-text'}
        ${activeSegment === seg ? 'bg-accent text-accent-foreground' : 'bg-transparent text-foreground'}`

    const separatorClass = 'select-none text-sm font-normal leading-5 text-foreground-muted'

    return (
        <div className={`flex min-w-[116px] flex-col gap-1 ${disabled ? 'opacity-50' : ''}`} style={style}>
            {showLabel && label && (
                <label
                    className={`inline-flex flex-wrap items-center gap-1 whitespace-nowrap pr-2 text-sm font-medium leading-[1.43] ${isInvalid ? 'text-danger' : 'text-foreground'}`}
                >
                    <span>{label}</span>
                    {required && <span className="text-danger">*</span>}
                </label>
            )}

            <div
                className={`box-border flex h-9 items-center gap-0.5 rounded-xl px-3 py-2 transition-[background,box-shadow] duration-150
                    focus-within:ring-2 focus-within:ring-accent
                    ${isSecondary ? 'bg-default' : 'bg-surface shadow-field'}
                    ${!isSecondary && !disabled ? 'not-focus-within:hover:bg-surface-secondary' : ''}
                    ${isInvalid ? 'ring-1 ring-danger' : ''}`}
            >
                <input
                    ref={hourRef}
                    readOnly
                    disabled={disabled}
                    value={pad(displayHour)}
                    onFocus={() => setActiveSegment('hour')}
                    onBlur={() => setActiveSegment(null)}
                    onKeyDown={(e) => handleSegmentKey('hour', e)}
                    className={segmentClass('hour')}
                />
                <span className={separatorClass}>:</span>
                <input
                    ref={minuteRef}
                    readOnly
                    disabled={disabled}
                    value={pad(value.minute)}
                    onFocus={() => setActiveSegment('minute')}
                    onBlur={() => setActiveSegment(null)}
                    onKeyDown={(e) => handleSegmentKey('minute', e)}
                    className={segmentClass('minute')}
                />
                {showSeconds && (
                    <>
                        <span className={separatorClass}>:</span>
                        <input
                            ref={secondRef}
                            readOnly
                            disabled={disabled}
                            value={pad(value.second)}
                            onFocus={() => setActiveSegment('second')}
                            onBlur={() => setActiveSegment(null)}
                            onKeyDown={(e) => handleSegmentKey('second', e)}
                            className={segmentClass('second')}
                        />
                    </>
                )}
                {is12 && (
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={togglePeriod}
                        onFocus={() => setActiveSegment('period')}
                        onBlur={() => setActiveSegment(null)}
                        className={`ml-1 rounded-sm border-none px-1 text-sm font-normal leading-5 outline-none
                            ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                            ${activeSegment === 'period' ? 'bg-accent text-accent-foreground' : 'bg-transparent text-foreground-muted'}`}
                    >
                        {period}
                    </button>
                )}
                <span className="ml-auto whitespace-nowrap text-sm font-normal leading-5 text-foreground-muted">{timezone}</span>
            </div>

            {showDescription &&
                (isInvalid && errorMessage ? (
                    <div className="p-1">
                        <p className="m-0 text-xs font-normal leading-[1.34] text-danger">{errorMessage}</p>
                    </div>
                ) : description ? (
                    <div className="p-1">
                        <p className="m-0 text-xs font-normal leading-[1.34] text-foreground-muted">{description}</p>
                    </div>
                ) : null)}
        </div>
    )
}
